import { Box, Flex, Stack } from '@chakra-ui/react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { Skeleton } from '@main/complykit/components/skeleton';
import { autoColor } from '@main/complykit/components/tag';
import { Text } from '@main/complykit/components/text';
import { localPoint } from '@visx/event';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { Key, MouseEvent, ReactNode, useRef } from 'react';

import {
  CHART_SEGMENT_TOOLTIP_OFFSET,
  ChartSegmentTooltipContent,
  useChartTooltipStyles,
} from '../charts/chart-segment-tooltip';
import { EmptyPlaceholder } from '../placeholder';
import { useChartScaleColors, useChartStructureColors } from '../status-tag/chart-colors';
import { DashboardCard, DashboardCardProps } from './dashboard-card';

export type HBarProps = {
  key: Key;
  label: string;
  value: number;
  color?: string;
  organizationId?: string;
  ownerId?: string;
};

export type HBarChartProps = DashboardCardProps & {
  heading: ReactNode;
  subheading?: ReactNode;
  isLoading?: boolean;
  bars?: HBarProps[];
  showBarTooltip?: boolean;
  onBarClick?: (bar: HBarProps) => void;
  loadingState: HBarChartLoadingProps;
  emptyState: HBarChartEmptyProps;
};

export const HBarChart = ({
  heading,
  subheading,
  isLoading,
  bars,
  showBarTooltip,
  onBarClick,
  loadingState,
  emptyState,
  ...props
}: HBarChartProps) => {
  const renderContents = () => {
    if (isLoading) {
      return <HBarChartLoading {...loadingState} />;
    }
    if (!bars || !bars.length) {
      return <HBarChartEmpty {...emptyState} />;
    }
    return (
      <DashboardCard.Body overflowY="auto">
        <Stack spacing={4}>
          {bars.map(({ key, ...bar }) => (
            <HBar
              key={key}
              barKey={key}
              {...bar}
              showBarTooltip={showBarTooltip}
              onBarClick={onBarClick}
            />
          ))}
        </Stack>
      </DashboardCard.Body>
    );
  };

  return (
    <DashboardCard {...props}>
      <DashboardCard.Header heading={heading} subheading={subheading} />
      {renderContents()}
    </DashboardCard>
  );
};

const HBar = ({
  barKey,
  value,
  label,
  color,
  organizationId,
  ownerId,
  showBarTooltip,
  onBarClick,
}: Omit<HBarProps, 'key'> & {
  barKey: Key;
  showBarTooltip?: boolean;
  onBarClick?: (bar: HBarProps) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { track: trackColor } = useChartStructureColors();
  /* A bar with no assigned color hashes its label onto the ComplyKit label
   * scales, the same autoColor the Tag chips use, and takes that scale's
   * soft chart fill (was a hashed Chakra .100 pastel). */
  const { fill: hashedFill } = useChartScaleColors(autoColor(label));
  const resolvedColor = color ?? hashedFill;
  const resolvedTooltipStyles = useChartTooltipStyles();
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } =
    useTooltip<{ label: string; percentage: number }>();
  const normValue = Math.max(0, Math.min(value, 1));
  const pctValue = Math.round(normValue * 100);
  const barInteractive = showBarTooltip || !!onBarClick;

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!barInteractive) {
      return;
    }

    const point = containerRef.current
      ? localPoint(containerRef.current, event)
      : localPoint(event);

    if (!point) {
      return;
    }

    showTooltip({
      tooltipData: { label, percentage: pctValue },
      tooltipLeft: point.x,
      tooltipTop: point.y,
    });
  };

  const handleClick = () => {
    if (!onBarClick) {
      return;
    }

    onBarClick({
      key: barKey,
      label,
      value,
      color: resolvedColor,
      organizationId,
      ownerId,
    });
  };

  return (
    <Stack spacing={1}>
      <Flex
        justify="space-between"
        fontSize="xs"
        lineHeight={4}
        color="gray.500"
        _dark={{ color: 'gray.200' }}
      >
        <Text size="xs">{label}</Text>
        <Text size="xs">{pctValue}%</Text>
      </Flex>
      <Box ref={containerRef} position="relative">
        <Box
          borderRadius="lg"
          overflow="hidden"
          bg={trackColor}
          cursor={barInteractive ? 'pointer' : undefined}
          onMouseMove={handleMouseMove}
          onMouseLeave={hideTooltip}
          onClick={handleClick}
        >
          <Box w={`${pctValue}%`} h={6} bg={resolvedColor} />
        </Box>
        {showBarTooltip && tooltipOpen && tooltipData ? (
          <TooltipWithBounds
            top={tooltipTop}
            left={tooltipLeft}
            style={resolvedTooltipStyles}
            {...CHART_SEGMENT_TOOLTIP_OFFSET}
          >
            <ChartSegmentTooltipContent
              label={tooltipData.label}
              value={`${tooltipData.percentage}%`}
            />
          </TooltipWithBounds>
        ) : null}
      </Box>
    </Stack>
  );
};

type HBarChartLoadingProps = {
  barsCount: number;
};

const HBarChartLoading = ({ barsCount }: HBarChartLoadingProps) => {
  return (
    <DashboardCard.Body overflowY="auto">
      <Stack spacing={4}>
        {[...Array(barsCount).keys()].map((key) => (
          <Stack key={key} spacing={1}>
            <Skeleton.Text size="xs" />
            <Skeleton.Block size="sm" />
          </Stack>
        ))}
      </Stack>
    </DashboardCard.Body>
  );
};

type HBarChartEmptyProps = {
  heading: ReactNode;
  subheading?: ReactNode;
};

const HBarChartEmpty = ({ heading, subheading }: HBarChartEmptyProps) => {
  return (
    <DashboardCard.Body display="flex" justifyContent="center" alignItems="center">
      <EmptyPlaceholder>
        <EmptyPlaceholder.Icon as={ChartBarIcon} />
        <EmptyPlaceholder.Content>
          <EmptyPlaceholder.Heading>{heading}</EmptyPlaceholder.Heading>
          <EmptyPlaceholder.Subheading>{subheading}</EmptyPlaceholder.Subheading>
        </EmptyPlaceholder.Content>
      </EmptyPlaceholder>
    </DashboardCard.Body>
  );
};
